import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "wouter";
import moment from "moment";
import Swal from "sweetalert2";
import {
  CheckCircleIcon, NoSymbolIcon, InformationCircleIcon,
  ChevronUpIcon, ChevronDownIcon, EyeIcon, PencilSquareIcon, TrashIcon
} from "@heroicons/react/24/outline";
import { AuthContext } from "../contexts/auth";
import { DataContext } from "../contexts/data";
import { getEmployeesWithActiveContract } from "../services/api_calendar";
import { exportEmployeeList } from "../services/api_extractions";
import { deleteDipendente } from "../services/api";
import EmployeesListControls from "./employees_list_controls";
import FilterModal from "./modal_filter_select";
import CustomTooltipComponent from "./custom_tooltip_component";
import PrettyPagination from "./pretty_pagination";
import Fetching from "./fetching";
import NoPermissions from "../authorization/NoPermissions";

const FILTER_BY = {
  all: "all",
  active: "contract_active",
  inactive: "contract_not_active",
  app_activate: "app_active",
  hr_active: "hr_active",
};

const EMPLOYEE_PERMISSIONS = [
  "hr.view_docs_ou",
  "hr.view_docs_all",
  "hr.manage_inouts_ou",
  "hr.manage_inouts_all",
  "hr.view_inouts_ou",
  "hr.view_inouts_all",
  "hr.manage_shifts_ou",
  "hr.manage_shifts_all",
  "hr.view_shifts_ou",
  "hr.view_shifts_all",
  "hr.manage_justification_ou",
  "hr.manage_justification_all",
  "hr.view_attendance_ou",
  "hr.view_attendance_all",
  "hr.manage_refunds_ou",
  "hr.manage_refunds_all",
];

const INITIAL_VISIBLE_COLUMNS = {
  name: true,
  email_auth: true,
  start_date: true,
  end_date: true,
};

const INITIAL_SORT = {
  key: null,
  direction: null,
};

export default function DipendentiTable({ token }) {
  const { profile } = useContext(AuthContext);
  const { setDipendentiGlobal } = useContext(DataContext);


  // DATI LISTA
  const [dipendenti, setDipendenti] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [totalResult, setTotalResult] = useState(0);

  // PAGINAZIONE
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // FILTRI
  const [filter, setFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(
    INITIAL_VISIBLE_COLUMNS
  );

  // DATE MODIFICATE NEL FORM
  const [contractStartDate, setContractStartDate] = useState("");
  const [contractEndDate, setContractEndDate] = useState("");

  // DATE EFFETTIVAMENTE APPLICATE ALLA RICERCA
  const [appliedDates, setAppliedDates] = useState({
    start: "",
    end: "",
  });

  // ORDINAMENTO: NULL UTILIZZA IL DEFAULT DEL BACKEND
  const [sortConfig, setSortConfig] = useState(INITIAL_SORT);

  // EXPORT
  const [showModal, setShowModal] = useState(false);
  const [filterSelected, setFilterSelected] = useState("");


  // PERMESSI
  const employeePermissions =
    profile?.employee?.permissions ?? [];

  const employeeHasPermission =
    profile?.customer_type === "employee" &&
    EMPLOYEE_PERMISSIONS.some(permission =>
      employeePermissions.includes(permission)
    );

  const canSeeTable =
    Boolean(profile) && (
      profile.customer_type !== "employee" || employeeHasPermission
    );


  // CARICAMENTO DIPENDENTI
  const fetchEmployees = useCallback(
    async (signal) => {
      if (!token || !canSeeTable) return;

      setFetching(true);
      setError(null);

      try {
        const response = await getEmployeesWithActiveContract(
          token,
          null,
          FILTER_BY[filter] || "all",
          signal,
          page,
          pageSize,
          null,
          "payrolls",
          appliedDates.start,
          appliedDates.end || null,
          sortConfig
        );

        if (signal?.aborted) return;

        const results = Array.isArray(response?.results)
          ? response.results
          : [];

        setDipendenti(results);
        setDipendentiGlobal(results);
        setTotalResult(response?.count ?? 0);
      } catch (err) {
        if (
          signal?.aborted ||
          err?.name === "AbortError" ||
          err?.name === "CanceledError" ||
          err?.code === "ERR_CANCELED"
        ) {
          return;
        }

        console.error("Errore caricamento dipendenti:", err);
        setError("Impossibile recuperare la lista dipendenti.");
      } finally {
        if (!signal?.aborted) {
          setFetching(false);
        }
      }
    },
    [
      token,
      canSeeTable,
      filter,
      page,
      pageSize,
      appliedDates.start,
      appliedDates.end,
      sortConfig,
      setDipendentiGlobal,
    ]
  );

  useEffect(() => {
    if (!token || !canSeeTable) {
      setFetching(false);
      return;
    }

    const controller = new AbortController();

    fetchEmployees(controller.signal);

    return () => controller.abort();
  }, [token, canSeeTable, fetchEmployees]);


  // FILTRO TIPOLOGIA
  const handleFilterChange = (newFilter) => {
    if (newFilter === filter) return;

    setFilter(newFilter);
    setSelectedEmployee(null);
    setPage(1);
  };


  // PAGINAZIONE
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };


  // RICERCA SINGOLO DIPENDENTE
  const getEmployeesForSearch = (
    tokenArg,
    query,
    filterBy = "all",
    ou,
    page = 1,
    pageSize = 10,
    permission,
    start,
    end,
    sortConfig
  ) => {
    return getEmployeesWithActiveContract(
      tokenArg,
      query,
      filterBy,
      null,
      page,
      pageSize,
      ou,
      permission,
      start,
      end,
      sortConfig
    );
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
  };


  // COLONNE VISIBILI
  const handleColumnVisibilityChange = (event) => {
    const { name, checked } = event.target;

    setVisibleColumns(prev => ({
      ...prev,
      [name]: checked,
    }));
  };


  // FILTRI DATE
  const handleSearchContractDates = () => {
    if (contractEndDate && !contractStartDate) {
      Swal.fire({
        icon: "warning",
        title: "Data Inizio richiesta",
        text: "Per usare la data di fine, devi inserire anche una data di inizio contratto.",
        confirmButtonText: "OK",
      });
      return;
    }

    const nextDates = {
      start: contractStartDate,
      end: contractEndDate,
    };

    setPage(1);

    if (
      appliedDates.start === nextDates.start &&
      appliedDates.end === nextDates.end
    ) {
      const controller = new AbortController();
      fetchEmployees(controller.signal);
      return;
    }

    setAppliedDates(nextDates);
  };


  // ORDINAMENTO
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction:
        prev.key === key && prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));

    setPage(1);
  };


  // MODALE ESPORTAZIONE
  const handleSelectFilters = () => {
    setFilterSelected(filter);
    setShowModal(true);
  };

  const handleExport = async ({
    filters,
    format,
    contractStartDate: exportStartDate,
    contractEndDate: exportEndDate,
  }) => {
    try {
      const { status, data } = await exportEmployeeList(
        token,
        format,
        [filters],
        FILTER_BY[filter] || "all",
        exportStartDate,
        exportEndDate || null
      );

      const alreadyExists =
        status === 200 &&
        String(data?.detail || "")
          .toLowerCase()
          .includes("già");

      Swal.fire({
        icon: alreadyExists ? "warning" : "success",
        title: alreadyExists
          ? "Esportazione già in corso"
          : "Esportazione avviata",
        html: `
          <div>
            ${
              alreadyExists
                ? "Esiste già un export per gli stessi parametri."
                : "Il file verrà generato e sarà scaricabile quando pronto."
            }
            <br/>
            <a
              href="/download_list"
              style="color:#4f46e5;text-decoration:underline;"
            >
              Vai alla pagina download
            </a>
          </div>
        `,
        confirmButtonText: "Chiudi",
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      Swal.fire({
        icon: status === 404 ? "info" : "error",
        title: status === 404 ? "Nessun dato" : "Errore",
        text:
          detail ||
          err?.message ||
          "Si è verificato un errore.",
      });
    }
  };


  // ELIMINAZIONE DIPENDENTE
  const handleDelete = async (cf) => {
    const result = await Swal.fire({
      title: "Sei sicuro?",
      html: `
        <p>
          Stai eliminando il dipendente
          <strong>${cf}</strong>.
          Questa azione non può essere annullata.
        </p>
        <p class="mt-3">
          Digita <strong>ELIMINA</strong> per confermare.
        </p>
      `,
      icon: "warning",
      input: "text",
      inputPlaceholder: "Digita ELIMINA",
      inputValidator: value =>
        value !== "ELIMINA"
          ? 'Digitare "ELIMINA" per procedere'
          : null,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Sì, elimina!",
      cancelButtonText: "Annulla",
    });

    if (!result.isConfirmed || result.value !== "ELIMINA") {
      return;
    }

    try {
      await deleteDipendente(token, cf);

      const updated = dipendenti.filter(dip => dip.cf !== cf);

      setDipendenti(updated);
      setDipendentiGlobal(updated);
      setTotalResult(prev => Math.max(0, prev - 1));

      if (selectedEmployee?.cf === cf) {
        setSelectedEmployee(null);
      }

      Swal.fire(
        "Eliminato!",
        "Il dipendente è stato eliminato.",
        "success"
      );

      if (updated.length === 0 && page > 1) {
        setPage(prev => prev - 1);
      }
    } catch (err) {
      console.error("Errore eliminazione dipendente:", err);

      Swal.fire(
        "Errore!",
        "Si è verificato un errore durante l'eliminazione.",
        "error"
      );
    }
  };


  // CONTESTO NAVIGAZIONE PRECEDENTE / SUCCESSIVO
  useEffect(() => {
    if (!canSeeTable || fetching || error) return;

    const employeesToDisplay = selectedEmployee
      ? [selectedEmployee]
      : dipendenti;

    const ctx = {
      page: String(page),
      page_size: String(pageSize),
      filter_by: FILTER_BY[filter] || "all",
      permission: "payrolls",

      ordering: sortConfig.key
        ? (
            sortConfig.direction === "descending"
              ? `-${sortConfig.key}`
              : sortConfig.key
          )
        : "",

      cs: appliedDates.start,
      ce: appliedDates.end,

      cfs: employeesToDisplay
        .map(employee => employee?.cf)
        .filter(Boolean),

      total_count: totalResult,
      single: Boolean(selectedEmployee),
      ts: Date.now(),
    };

    sessionStorage.setItem(
      "employees_nav_ctx",
      JSON.stringify(ctx)
    );
  }, [
    canSeeTable,
    fetching,
    error,
    page,
    pageSize,
    filter,
    sortConfig,
    appliedDates,
    selectedEmployee,
    dipendenti,
    totalResult,
  ]);


  // INFORMAZIONI STATO LOGIN
  const showLoginRequirements = () => {
    Swal.fire({
      title: "Requisiti per il Login",
      html: `
        <p>
          Il dipendente, per effettuare il login, deve
          soddisfare i seguenti requisiti:
        </p>
        <br/>
        <p>
          L'app deve essere attiva.
          <br/><br/>
          Il contratto deve essere attivo oppure
          cessato da non più di 18 mesi.
        </p>
        <br/>
        <p>
          L'attivazione dell'app e la gestione
          dei contratti sono disponibili nella
          scheda del dipendente.
        </p>
      `,
      icon: "info",
      confirmButtonText: "OK",
    });
  };


  // RENDER
  if (!canSeeTable) {
    return <NoPermissions />;
  }

  const displayedEmployees = selectedEmployee
    ? [selectedEmployee]
    : dipendenti;

  const visibleColumnCount =
    3 +
    Number(visibleColumns.name) +
    Number(visibleColumns.email_auth) +
    Number(visibleColumns.start_date) +
    Number(visibleColumns.end_date);

  return (
    <>
      {/* INTESTAZIONE E FILTRI */}
      <EmployeesListControls
        totalResult={totalResult}
        filter={filter}
        onFilterChange={handleFilterChange}

        token={token}
        selectedEmployee={selectedEmployee}
        getEmployees={getEmployeesForSearch}
        onSelectEmployee={handleSelectEmployee}

        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}

        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(prev => !prev)}

        visibleColumns={visibleColumns}
        onColumnChange={handleColumnVisibilityChange}

        contractStartDate={contractStartDate}
        contractEndDate={contractEndDate}
        onContractStartDateChange={setContractStartDate}
        onContractEndDateChange={setContractEndDate}
        onSearchDates={handleSearchContractDates}

        onExport={handleSelectFilters}
      />

      {/* TABELLA ORIGINALE: layout e responsive invariati */}
      <div className="flex items-center">
        <table className="min-w-full min-h-full border border-gray-200 rounded-lg">
          <thead className="sticky top-0 bg-white" style={{ zIndex: '5'}}>
            <tr className="bg-gray-50">
              {visibleColumns.name && (
                <th
                  scope="col"
                  colSpan={1}
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell whitespace-normal"
                  style={{ width: '20%' }}
                >
                  Cognome e Nome
                  <button
                    onClick={() => handleSort('last_name')}
                    className="ml-2 text-gray-500 hover:text-gray-900"
                  >
                    {sortConfig.key === 'last_name' && sortConfig.direction === 'ascending' ? (
                      <CustomTooltipComponent title="Decrescente" placement="bottom" arrow>
                        <ChevronUpIcon className="w-4 h-4" />
                      </CustomTooltipComponent>
                    ) : (
                      <CustomTooltipComponent title="Crescente" placement="bottom" arrow>
                        <ChevronDownIcon className="w-4 h-4" />
                      </CustomTooltipComponent>
                    )}
                  </button>
                </th>
              )}
              <th
                scope="col"
                colSpan={1}
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                style={{ width: '15%' }}
              >
                <div className="flex items-center">
                  Codice Fiscale
                  <button
                    onClick={() => handleSort('cf')}
                    className="ml-2 text-gray-500 hover:text-gray-900"
                  >
                    {sortConfig.key === 'cf' && sortConfig.direction === 'ascending' ? (
                      <CustomTooltipComponent title="Decrescente" placement="bottom" arrow>
                        <ChevronUpIcon className="w-4 h-4" />
                      </CustomTooltipComponent>
                    ) : (
                      <CustomTooltipComponent title="Crescente" placement="bottom" arrow>
                        <ChevronDownIcon className="w-4 h-4" />
                      </CustomTooltipComponent>
                    )}
                  </button>
                </div>
              </th>
              {visibleColumns.email_auth && (
                <th
                  scope="col"
                  colSpan={1}
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell"
                  style={{ width: '15%' }}
                >
                  Email
                  <button
                    onClick={() => handleSort('email_auth')}
                    className="ml-2 text-gray-500 hover:text-gray-900"
                  >
                    {sortConfig.key === 'email_auth' && sortConfig.direction === 'ascending' ? (
                      <CustomTooltipComponent title="Decrescente" placement="bottom" arrow>
                        <ChevronUpIcon className="w-4 h-4" />
                      </CustomTooltipComponent>
                    ) : (
                      <CustomTooltipComponent title="Crescente" placement="bottom" arrow>
                        <ChevronDownIcon className="w-4 h-4" />
                      </CustomTooltipComponent>
                    )}
                  </button>
                </th>
              )}
              {visibleColumns.start_date && (
                <th
                  scope="col"
                  colSpan={1}
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell whitespace-normal"
                  style={{ width: '10%' }}
                >
                  <div className="flex items-center">
                    Data Inizio
                    <button
                      onClick={() => handleSort('start_date')}
                      className="ml-2 text-gray-500 hover:text-gray-900"
                    >
                      {sortConfig.key === 'start_date' && sortConfig.direction === 'ascending' ? (
                        <CustomTooltipComponent title="Decrescente" placement="bottom" arrow>
                          <ChevronUpIcon className="w-4 h-4" />
                        </CustomTooltipComponent>
                      ) : (
                        <CustomTooltipComponent title="Crescente" placement="bottom" arrow>
                          <ChevronDownIcon className="w-4 h-4" />
                        </CustomTooltipComponent>
                      )}
                    </button>
                  </div>
                </th>
              )}
              {visibleColumns.end_date && (
                <th
                  scope="col"
                  colSpan={1}
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell whitespace-normal"
                  style={{ width: '10%' }}
                >
                  <div className="flex items-center">
                    Data Fine
                    <button
                      onClick={() => handleSort('end_date')}
                      className="ml-2 text-gray-500 hover:text-gray-900"
                    >
                      {sortConfig.key === 'end_date' && sortConfig.direction === 'ascending' ? (
                        <CustomTooltipComponent title="Decrescente" placement="bottom" arrow>
                          <ChevronUpIcon className="w-4 h-4" />
                        </CustomTooltipComponent>
                      ) : (
                        <CustomTooltipComponent title="Crescente" placement="bottom" arrow>
                          <ChevronDownIcon className="w-4 h-4" />
                        </CustomTooltipComponent>
                      )}
                    </button>
                  </div>
                </th>
              )}
              <th
                scope="col"
                colSpan={1}
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                style={{ width: '10%' }}
                onClick={() => {
                  Swal.fire({
                    title: 'Requisiti per il Login',
                    html: `
                      <p>
                        Il dipendente, per poter effettuare il login, ha necessità dei seguenti requisiti:
                      </p><br/>
                      <p>
                        L'app deve essere attiva.<br/><br/>
                        Il contratto deve essere attivo oppure cessato da non più di 18 mesi.
                      </p><br/>
                      <p>
                        L'attivazione dell'app e l'aggiunta di un contratto possono essere gestite nella scheda di dettaglio del dipendente.
                      </p>
                    `,
                    icon: 'info',
                    confirmButtonText: 'OK',
                  });
                }}
              >
                Stato Login
                <InformationCircleIcon className="inline-flex w-5 h-5 ml-1 align-center text-cyan-500" />
              </th>
              <th
                scope="col"
                colSpan={1}
                className="px-0 py-3.5 text-left text-sm font-semibold text-gray-900"
                style={{ width: '10%' }}
              >
                  <button
                    onClick={() => handleSort('login_status')}
                    className="text-gray-500 hover:text-gray-900"
                  >
                    {sortConfig.key === 'login_status' && sortConfig.direction === 'ascending' ? (
                      <CustomTooltipComponent title="Decrescente" placement="bottom" arrow>
                        <ChevronUpIcon className="w-4 h-4" />
                      </CustomTooltipComponent>
                    ) : (
                      <CustomTooltipComponent title="Crescente" placement="bottom" arrow>
                        <ChevronDownIcon className="w-4 h-4" />
                      </CustomTooltipComponent>
                    )}
                  </button>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {(selectedEmployee ? 
              [selectedEmployee] : dipendenti
            ).map((dip) => (
              <React.Fragment key={dip?.id || dip?.cf || 'selected-employee'}>
                <tr className="border-gray-200 border-t">
                  {visibleColumns.name && (
                    <td className="whitespace-normal px-3 py-4 text-sm text-gray-500 hidden lg:table-cell" style={{ width: '20%' }}>
                      {`${dip?.last_name ?? ''} ${dip?.first_name ?? ''}`}
                    </td>
                  )}
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" style={{ width: '15%' }}>
                    {dip?.cf}
                  </td>
                  {visibleColumns.email_auth && (
                    <td className="whitespace-normal px-3 py-4 text-sm text-gray-500 hidden lg:table-cell" style={{ width: '15%', wordBreak: 'break-word' }}>
                      {dip?.email_auth}
                    </td>
                  )}
                  {visibleColumns.start_date && (
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 hidden lg:table-cell" style={{ width: '10%' }}>
                      {dip?.active_contract 
                        ? (dip?.active_contract?.start_date ? moment(dip?.active_contract.start_date).format('DD/MM/YYYY') : '')
                        : (dip?.last_contract?.start_date ? moment(dip.last_contract.start_date).format('DD/MM/YYYY') : '')
                      }
                    </td>
                  )}
                  {visibleColumns.end_date && (
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 hidden lg:table-cell" style={{ width: '10%' }}>
                      {dip?.active_contract 
                        ? (dip?.active_contract?.end_date ? moment(dip?.active_contract.end_date).format('DD/MM/YYYY') : '')
                        : (dip?.last_contract?.end_date ? moment(dip?.last_contract.end_date).format('DD/MM/YYYY') : '')
                      }
                    </td>
                  )}
              
                  <td
                    className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3"
                    style={{ width: '10%' }}
                  >
                    <div className="flex flex-col items-start space-y-2">
                      <div className="group relative flex items-center justify-center">
                        {(() => {
                          const addMonths = (date, m) =>
                            new Date(date.getFullYear(), date.getMonth() + m, date.getDate());

                          const today = new Date();
                          today.setHours(0, 0, 0, 0);

                          const appActivate =
                            dip?.app_activate === true || dip?.app_activate === 'true';

                          const active = Boolean(dip?.active_contract);
                          const ended  = !active && Boolean(dip?.last_contract?.end_date);

                          let contractType = '';
                          let accessUntil = null; // data limite (end_date + 18 mesi, inclusiva)
                          let contractAllowsAccess = false;

                          if (active) {
                            contractType = 'attivo';
                            contractAllowsAccess = true;
                          } else if (ended) {
                            const endDate = new Date(dip.last_contract.end_date);
                            accessUntil = addMonths(endDate, 18);
                            if (today <= accessUntil) {
                              contractType = 'cessato negli ultimi 18 mesi';
                              contractAllowsAccess = true;    // entro 18 mesi: contratto OK
                            } else {
                              contractType = 'cessato oltre 18 mesi fa';
                              contractAllowsAccess = false;   // oltre 18 mesi: contratto KO
                            }
                          } else {
                            contractType = 'nessun contratto';
                            contractAllowsAccess = false;
                          }

                          // Accesso consentito SOLO se app attiva E contratto consente
                          const canAccess = appActivate && contractAllowsAccess;

                          const fmt = d => d
                            ? d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : '-';

                          return canAccess ? (
                            <>
                              <CheckCircleIcon className="h-5 w-5 text-green-500 opacity-100 group-hover:opacity-50 transition-opacity" />
                              <button
                                onClick={() => {
                                  Swal.fire({
                                    title: 'Dettaglio Accesso',
                                    html: `
                                      <p>Contratto attivo o cessato negli ultimi 18 mesi: <br/><strong>${contractType}</strong>${
                                        accessUntil ? `<br/>(accesso consentito fino al <strong>${fmt(accessUntil)}</strong>)` : ''
                                      }</p><br/>
                                      <p>Accesso App Abilitato: <br/><strong>Sì</strong></p>
                                    `,
                                    icon: 'info',
                                    confirmButtonText: 'OK',
                                  });
                                }}
                                className="absolute flex items-center justify-center px-4 py-2 text-center text-sm text-gray-700 bg-green-50 hidden group-hover:flex text-wrap cursor-pointer"
                              >
                                Può accedere all'app
                              </button>
                            </>
                          ) : (
                            <>
                              <NoSymbolIcon className="h-5 w-5 text-red-500 opacity-100 group-hover:opacity-50 transition-opacity" />
                              <button
                                onClick={() => {
                                  Swal.fire({
                                    title: 'Dettaglio Accesso',
                                    html: `
                                      <p>Contratto attivo o cessato negli ultimi 18 mesi: <br/><strong>${contractType}</strong>${
                                        accessUntil ? `<br/>(accesso consentito fino al <strong>${fmt(accessUntil)}</strong>)` : ''
                                      }</p><br/>
                                      <p>Accesso App Abilitato: <br/><strong>${appActivate ? 'Sì' : 'No'}</strong></p>
                                    `,
                                    icon: 'info',
                                    confirmButtonText: 'OK',
                                  });
                                }}
                                className="absolute flex items-center justify-center px-4 py-2 text-center text-sm text-gray-700 bg-red-50 hidden group-hover:flex text-wrap cursor-pointer z-10"
                              >
                                Non può accedere all'app
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </td>
                  <td className="relative whitespace-nowrap px-3 py-4 text-right text-sm font-medium" style={{ width: "10%" }}>
                    <div className="flex items-center justify-center gap-2">

                      {/* APRI DIPENDENTE */}
                      <CustomTooltipComponent title="Apri dipendente" placement="top" arrow>
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-indigo-50 hover:text-indigo-600">
                          <Link
                            to={`/dipendenti/${dip?.cf}`}
                            aria-label="Apri dipendente"
                            className="inline-flex h-full w-full items-center justify-center rounded-full text-inherit"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </Link>
                        </span>
                      </CustomTooltipComponent>

                      {/* MODIFICA DIPENDENTE */}
                      <CustomTooltipComponent title="Modifica dipendente" placement="top" arrow>
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-indigo-50 hover:text-indigo-600">
                          <Link
                            to={`/dipendenti/${dip?.cf}/edit`}
                            aria-label="Modifica dipendente"
                            className="inline-flex h-full w-full items-center justify-center rounded-full text-inherit"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </Link>
                        </span>
                      </CustomTooltipComponent>

                      {/* ELIMINA DIPENDENTE */}
                      <CustomTooltipComponent title="Elimina dipendente" placement="top" arrow>
                        <button
                          type="button"
                          onClick={() => handleDelete(dip?.cf)}
                          aria-label="Elimina dipendente"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </CustomTooltipComponent>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}

            {!selectedEmployee && dipendenti.length === 0 && (
              <tr>
                <td colSpan={visibleColumnCount} className="m-8 rounded-lg border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 text-sm font-semibold text-gray-900">
                  <div className="text-center py-4">Non sono presenti dati</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINAZIONE */}
      {!selectedEmployee && (
        <PrettyPagination
          page={page}
          totalPages={Math.ceil(totalResult / pageSize)}
          totalCount={totalResult}
          onChange={setPage}
          labelSingolare="dipendente"
          labelPlurale="dipendenti"
          className="rounded-b-md border border-gray-200 bg-gray-50 py-3"
        />
      )}

      {/* MODALE EXPORT */}
      {showModal && (
        <FilterModal
          onClose={() => setShowModal(false)}
          onApply={handleExport}
          filtersSelected={filterSelected}
          startDate={appliedDates.start}
          endDate={appliedDates.end}
        />
      )}
    </>
  );
}
